import React, { useState } from 'react';
import { businessService } from '../../services/businessService';
import toast from 'react-hot-toast';

// Import the same constants from AccountPage
const BUSINESS_STAGES = [
  { value: 'idea', label: 'Idea Stage', description: 'Concept validation, no revenue yet' },
  { value: 'mvp', label: 'MVP/Launch', description: 'First customers, testing product-market fit' },
  { value: 'growth', label: 'Growth', description: 'Scaling revenue, proven business model' },
  { value: 'mature', label: 'Mature', description: 'Established business, optimizing operations' },
  { value: 'pivot', label: 'Pivoting', description: 'Changing strategy or product direction' }
];

const INDUSTRIES = [
  'SaaS/Software',
  'E-commerce',
  'Professional Services',
  'Healthcare',
  'Education/EdTech',
  'FinTech',
  'MarTech',
  'Real Estate',
  'Manufacturing',
  'Retail',
  'Consulting',
  'Other'
];

const TARGET_MARKETS = [
  { value: 'b2b_smb', label: 'B2B SMB (1-50 employees)' },
  { value: 'b2b_mid', label: 'B2B Mid-market (50-500 employees)' },
  { value: 'b2b_enterprise', label: 'B2B Enterprise (500+ employees)' },
  { value: 'b2c_mass', label: 'B2C Mass Market' },
  { value: 'b2c_niche', label: 'B2C Niche/Premium' },
  { value: 'b2b2c', label: 'B2B2C/Platform' }
];

const GEOGRAPHIES = [
  { value: 'uk', label: 'United Kingdom' },
  { value: 'eu', label: 'European Union' },
  { value: 'us', label: 'United States' },
  { value: 'us_ca', label: 'US & Canada' },
  { value: 'global', label: 'Global/Multi-region' },
  { value: 'apac', label: 'Asia-Pacific' },
  { value: 'other', label: 'Other' }
];

const TEAM_SIZES = [
  { value: '1', label: 'Solo founder' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-10', label: '6-10 people' },
  { value: '11-20', label: '11-20 people' },
  { value: '21+', label: '21+ people' }
];

interface BusinessProfileWizardProps {
  onComplete: () => void;
  onSkip: () => void;
}

export const BusinessProfileWizard: React.FC<BusinessProfileWizardProps> = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [stage, setStage] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('');
  const [geography, setGeography] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [goals, setGoals] = useState('');
  const [mainChallenge, setMainChallenge] = useState('');

  const totalSteps = 4;

  const canProceed = () => {
    switch (step) {
      case 1:
        return name.trim() && industry && stage;
      case 2:
        return targetCustomer && geography;
      case 3:
        return true; // Optional fields
      case 4:
        return true; // Preview step
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      await businessService.create({
        name: name.trim(),
        industry: industry,
        stage: stage,
        target_customer: targetCustomer,
        geography: geography,
        team_size: teamSize || undefined,
        goals: goals || undefined,
        pain_points: mainChallenge ? [mainChallenge] : undefined,
        is_default: true,
        completed_onboarding: true,
      } as any);

      toast.success('Business profile created! 🎉');
      onComplete();
    } catch (error: any) {
      console.error('Failed to create business:', error);
      toast.error(error?.message || 'Failed to create business profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {step === 4 ? '🎉 Almost Done!' : '👋 Welcome to Explanatum'}
            </h2>
            <button
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={isSubmitting}
            >
              Skip for now
            </button>
          </div>
          <p className="text-sm text-gray-600">
            {step === 4 
              ? "Review your profile and see how we'll personalize your experience"
              : "Let's set up your business profile so we can give you personalized advice"}
          </p>
          
          {/* Progress bar */}
          <div className="mt-4 flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  s <= step ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">Step {step} of {totalSteps}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Let's start with the basics
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Tell us about your business so we can tailor our advice to your needs.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Acme Ltd"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry *
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  <option value="">Select your industry...</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Stage *
                </label>
                <div className="space-y-2">
                  {BUSINESS_STAGES.map((s) => (
                    <label
                      key={s.value}
                      className={`block p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        stage === s.value
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="stage"
                        value={s.value}
                        checked={stage === s.value}
                        onChange={(e) => setStage(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{s.label}</p>
                          <p className="text-sm text-gray-600 mt-1">{s.description}</p>
                        </div>
                        {stage === s.value && (
                          <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Target Market */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Who are you building for?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Understanding your target market helps us recommend the right strategies.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Market *
                </label>
                <div className="space-y-2">
                  {TARGET_MARKETS.map((market) => (
                    <label
                      key={market.value}
                      className={`block p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                        targetCustomer === market.value
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="targetCustomer"
                        value={market.value}
                        checked={targetCustomer === market.value}
                        onChange={(e) => setTargetCustomer(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{market.label}</span>
                        {targetCustomer === market.value && (
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Geography *
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={geography}
                  onChange={(e) => setGeography(e.target.value)}
                >
                  <option value="">Select primary region...</option>
                  {GEOGRAPHIES.map((geo) => (
                    <option key={geo.value} value={geo.value}>{geo.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size (optional)
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                >
                  <option value="">Select team size...</option>
                  {TEAM_SIZES.map((size) => (
                    <option key={size.value} value={size.value}>{size.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 3: Goals & Challenges */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  What success would look like?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Share your goals and challenges so we can focus on what matters most.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Top Goal (next 6 months)
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="e.g., Reach £10K MRR, launch paid tier, raise seed funding"
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Optional but highly recommended</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Biggest Challenge Right Now
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="e.g., Low conversion rate, high customer acquisition cost, struggling with visibility"
                  value={mainChallenge}
                  onChange={(e) => setMainChallenge(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Optional but highly recommended</p>
              </div>
            </div>
          )}

          {/* Step 4: Preview */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Your Business Profile
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Here's how we'll use this information to personalize your experience:
                </p>
              </div>

              {/* Profile Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Business</p>
                  <p className="font-medium text-gray-900">{name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Industry</p>
                    <p className="font-medium text-gray-900">{industry}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Stage</p>
                    <p className="font-medium text-gray-900 capitalize">{stage}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Target Market</p>
                    <p className="font-medium text-gray-900">
                      {TARGET_MARKETS.find(m => m.value === targetCustomer)?.label}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Geography</p>
                    <p className="font-medium text-gray-900">
                      {GEOGRAPHIES.find(g => g.value === geography)?.label}
                    </p>
                  </div>
                </div>
                {goals && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Top Goal</p>
                    <p className="text-sm text-gray-900">{goals}</p>
                  </div>
                )}
                {mainChallenge && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Main Challenge</p>
                    <p className="text-sm text-gray-900">{mainChallenge}</p>
                  </div>
                )}
              </div>

              {/* Example personalization */}
              <div className="border-l-4 border-blue-600 bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  💡 How this helps you:
                </p>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li>✅ <strong>Business Consultant</strong> will reference your {industry} business at {stage} stage</li>
                  <li>✅ <strong>Marketing tools</strong> will suggest strategies for {TARGET_MARKETS.find(m => m.value === targetCustomer)?.label}</li>
                  <li>✅ <strong>Financial tools</strong> will use {stage}-appropriate benchmarks</li>
                  {goals && <li>✅ All advice will align with your goal: "{goals.slice(0, 50)}..."</li>}
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> You can always update your profile later in Account Settings.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={step === 1 || isSubmitting}
          >
            ← Back
          </button>

          <div className="flex gap-3">
            {step < totalSteps ? (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!canProceed()}
              >
                Continue →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Complete Setup 🎉'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
